"use client";

import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { FC, ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";
import Button from "./Button";
import DropdownMenu from "./DropdownMenu";

// The publication card's corner "…": the 28px disc that sits on the p-4
// padding edge, overhanging the 24px avatar row by 4px below (see the
// PublicationForm trigger comment). One definition so a caller that
// supplies the card's menu from outside wears the same disc in the same
// slot as the card's own menus.
//
// Media controls use a ghost button without the card corner's
// negative margin.

/** The card's own hover-reveal policy (desktop hides the disc until the
 *  card is hovered or focused; open menus stay visible). */
const cornerMenuRevealClass = (isOpen: boolean) =>
  isOpen
    ? "opacity-100 pointer-events-auto"
    : "opacity-100 pointer-events-auto md:opacity-0 md:pointer-events-none md:group-hover:opacity-100 md:group-hover:pointer-events-auto md:group-focus-within:opacity-100 md:group-focus-within:pointer-events-auto";

export const CORNER_MENU_TRIGGER_CLASS =
  "!w-7 !min-w-7 !h-7 !p-0 -mb-1 self-start shrink-0";

const PublicationCardCornerMenu: FC<{
  ariaLabel: string;
  /**
   * "hover" = the card stack's policy (revealed on hover/focus at md+);
   * "always" = a lone card whose menu is its only way to act — a popover.
   */
  reveal?: "hover" | "always";
  /**
   * "card" = inset card corner, "media" = ghost over video. @default "card"
   */
  tone?: "card" | "media";
  children: (args: { close: () => void }) => ReactNode;
}> = ({ ariaLabel, reveal = "hover", tone = "card", children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMedia = tone === "media";
  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={setIsOpen}
      // The panel's size, NOT the trigger's: PopoverPannel derives the
      // panel's radius from it, and that radius has to nest the items'
      // (14px = the size-m item's 10px + the p-1 inset). At "s" the panel
      // wore an 11px corner around 10px items, so the corner read tighter
      // than the four straight edges. The disc below stays size "s".
      size="m"
      positions={["bottom", "top"]}
      align="end"
    >
      <DropdownMenu.Trigger>
        <Button
          type="button"
          size="s"
          variant={isMedia ? "ghost" : "tertiary"}
          aria-label={ariaLabel}
          className={twMerge(
            tone === "card" ? CORNER_MENU_TRIGGER_CLASS : "shrink-0",
            reveal === "hover" ? cornerMenuRevealClass(isOpen) : undefined,
          )}
        >
          <EllipsisHorizontalIcon className="w-4 h-4" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>{children}</DropdownMenu.Content>
    </DropdownMenu>
  );
};

export default PublicationCardCornerMenu;
